/**
 * Pure transitions for the bulk ROI selection set. No Recoil, no React.
 *
 * The selection is valid only when empty or of size >= 2; any transition that
 * would land on size 1 collapses to the empty set so the caller can fall
 * through to single-selection behavior.
 */
import { RoiId } from '~/domain/changes/ids';

const EMPTY: ReadonlySet<RoiId> = new Set();

/**
 * Toggle membership of `clicked` (ctrl/cmd-click semantics).
 *
 * The working set seeds from `current` when non-empty, otherwise from the
 * active ROI when one is present. A result smaller than two members collapses
 * to empty, signalling the caller to fall through to plain-click behavior.
 */
export function applyCtrlToggle(
    current: ReadonlySet<RoiId>,
    activeRoiId: RoiId | null,
    clicked: RoiId
): ReadonlySet<RoiId> {
    const working = new Set<RoiId>(
        current.size > 0 ? current : activeRoiId !== null ? [activeRoiId] : []
    );

    if (working.has(clicked)) {
        working.delete(clicked);
    } else {
        working.add(clicked);
    }

    if (working.size < 2) {
        return EMPTY;
    }
    return working;
}

/**
 * Replace the selection with the inclusive range between `anchor` and
 * `clicked` over `visualOrder` (shift-click semantics). A null or absent anchor
 * falls back to `clicked`. A degenerate range of a single member collapses to
 * empty.
 */
export function applyShiftRange(
    visualOrder: readonly RoiId[],
    anchor: RoiId | null,
    clicked: RoiId
): ReadonlySet<RoiId> {
    const clickedIndex = visualOrder.indexOf(clicked);
    if (clickedIndex === -1) {
        return EMPTY;
    }

    const anchorIndex = anchor === null ? -1 : visualOrder.indexOf(anchor);
    const effectiveAnchorIndex =
        anchorIndex === -1 ? clickedIndex : anchorIndex;

    const start = Math.min(effectiveAnchorIndex, clickedIndex);
    const end = Math.max(effectiveAnchorIndex, clickedIndex);
    const range = new Set<RoiId>(visualOrder.slice(start, end + 1));

    if (range.size < 2) {
        return EMPTY;
    }
    return range;
}

/**
 * Choose the active ROI after a bulk-set transition so the active-row highlight
 * never points outside a non-empty set. Keeps the current active ROI when it
 * survived, else lands on the just-clicked member (the shift endpoint), else the
 * first surviving member in visual order. Returns null for an empty set.
 */
export function reconcileActiveRoi(
    nextSet: ReadonlySet<RoiId>,
    clicked: RoiId,
    currentActive: RoiId | null,
    visualOrder: readonly RoiId[]
): RoiId | null {
    if (nextSet.size === 0) {
        return null;
    }
    if (currentActive !== null && nextSet.has(currentActive)) {
        return currentActive;
    }
    if (nextSet.has(clicked)) {
        return clicked;
    }
    for (const roiId of visualOrder) {
        if (nextSet.has(roiId)) {
            return roiId;
        }
    }
    // Every member is filtered out of the visual order; fall back to any.
    return nextSet.values().next().value ?? null;
}

/**
 * Drop selected members that no longer exist. Returns the SAME reference when
 * nothing is pruned (so selection effects don't loop). Collapses to empty when
 * fewer than two members survive.
 */
export function pruneSelection(
    current: ReadonlySet<RoiId>,
    existing: ReadonlySet<RoiId>
): ReadonlySet<RoiId> {
    let removed = false;
    const kept = new Set<RoiId>();
    for (const roiId of current) {
        if (existing.has(roiId)) {
            kept.add(roiId);
        } else {
            removed = true;
        }
    }

    if (!removed) {
        return current;
    }
    if (kept.size < 2) {
        return EMPTY;
    }
    return kept;
}

/**
 * Toggle membership of `clicked` while multi-select MODE is armed.
 *
 * Unlike {@link applyCtrlToggle} this neither seeds from the active
 * structure nor collapses below two members: in mode the selection is
 * built up from nothing, one structure at a time, and every intermediate
 * size is a state the user is deliberately in. Seeding from the active
 * structure would tick a checkbox the user never ticked.
 */
export function applyModeToggle(
    current: ReadonlySet<RoiId>,
    clicked: RoiId
): ReadonlySet<RoiId> {
    const working = new Set<RoiId>(current);
    if (working.has(clicked)) {
        working.delete(clicked);
    } else {
        working.add(clicked);
    }
    return working;
}
